import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { CheckCircle2, Info, AlertTriangle, X } from "lucide-react";

type ToastKind = "success" | "info" | "warn" | "error";

interface ToastItem {
  id: number;
  kind: ToastKind;
  message: string;
}

interface ToastApi {
  push: (message: string, kind?: ToastKind, durationMs?: number) => void;
}

const ToastCtx = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const idRef = useRef(1);

  const dismiss = useCallback((id: number) => {
    setItems(prev => prev.filter(t => t.id !== id));
  }, []);

  const push = useCallback((message: string, kind: ToastKind = "info", durationMs = 3500) => {
    const id = idRef.current++;
    setItems(prev => [...prev, { id, kind, message }]);
    window.setTimeout(() => dismiss(id), durationMs);
  }, [dismiss]);

  return (
    <ToastCtx.Provider value={{ push }}>
      {children}
      <div
        className="fixed bottom-4 right-4 z-[100] flex flex-col gap-xs pointer-events-none"
        role="region"
        aria-label="Notifications"
      >
        {items.map(t => (
          <ToastView key={t.id} item={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

function ToastView({ item, onDismiss }: { item: ToastItem; onDismiss: () => void }) {
  const tone: Record<ToastKind, { bg: string; text: string; Icon: typeof Info }> = {
    success: { bg: "bg-success-bg", text: "text-success-text", Icon: CheckCircle2 },
    info:    { bg: "bg-info-bg",    text: "text-info-text",    Icon: Info },
    warn:    { bg: "bg-warning-bg", text: "text-warning-text", Icon: AlertTriangle },
    error:   { bg: "bg-danger-bg",  text: "text-danger-text",  Icon: AlertTriangle },
  };
  const { bg, text, Icon } = tone[item.kind];
  return (
    <div
      className={`pointer-events-auto flex items-start gap-sm rounded-md border border-border ${bg} ${text} shadow-card px-md py-sm text-sm min-w-[240px] max-w-[360px]`}
      role="status"
    >
      <Icon size={16} className="mt-[2px] shrink-0" />
      <div className="flex-1">{item.message}</div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="opacity-70 hover:opacity-100 cursor-pointer"
      >
        <X size={14} />
      </button>
    </div>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastCtx);
  if (!ctx) {
    // Fail soft so a forgotten provider doesn't crash a page — log once.
    return {
      push: (msg) => {
        if (typeof window !== "undefined") console.warn("[Toast] no provider:", msg);
      },
    };
  }
  return ctx;
}
